import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth, apiError } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { affT } from "../lib/affI18n";
import { money } from "../lib/format";
import { Spinner } from "../lib/ui";
import { Share2, Copy, Users, MousePointerClick, BadgeCheck, Wallet, Gift } from "lucide-react";

const PEOPLE = [5, 10, 25];

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="card" data-testid={testid} style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--brass)", marginBottom: 6 }}>
        <Icon size={16} /><span className="eyebrow">{label}</span>
      </div>
      <div className="num" style={{ fontSize: 24, color: "var(--pine)" }}>{value}</div>
    </div>
  );
}

function BalanceCard({ A, balances, det, minPayout, onGoToForm, nextMonth }) {
  const curs = Object.keys(balances || {});
  if (!curs.length) return null;
  const min = minPayout || 20;
  const hasPayoutData = !!(det && (det.has_iban || det.has_paypal));
  const anyReady = curs.some((c) => (balances[c].available || 0) >= min);
  return (
    <div className="card" data-testid="affiliate-earnings" style={{ padding: 16 }}>
      <div className="eyebrow" style={{ marginBottom: 12 }}>{A.earningsTitle}</div>
      {curs.map((c) => {
        const b = balances[c];
        const total = Math.round(((b.pending || 0) + (b.available || 0) + (b.paid || 0)) * 100) / 100;
        const avail = b.available || 0;
        const ready = avail >= min;
        const pct = Math.min(100, Math.round((avail / min) * 100));
        const missing = Math.max(0, Math.round((min - avail) * 100) / 100);
        return (
          <div key={c} data-testid={`balance-${c}`} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", marginBottom: 6 }}>{c}</div>
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 13.5, marginBottom: 10 }}>
              <span>{A.eTotal}: <b className="num" data-testid={`bal-total-${c}`}>{money(total, c)}</b></span>
              <span>{A.ePending}: <b className="num" data-testid={`bal-pending-${c}`}>{money(b.pending, c)}</b></span>
              <span>{A.eAvailable}: <b className="num" style={{ color: "var(--pine)" }} data-testid={`bal-available-${c}`}>{money(avail, c)}</b></span>
              <span>{A.ePaid}: <b className="num" data-testid={`bal-paid-${c}`}>{money(b.paid, c)}</b></span>
            </div>
            <div style={{ height: 8, borderRadius: 6, background: "var(--line)", overflow: "hidden" }}>
              <div data-testid={`progress-${c}`} style={{ width: `${pct}%`, height: "100%", background: ready ? "var(--pine)" : "var(--brass)", transition: "width .4s ease" }} />
            </div>
            {ready ? (
              <>
                <p data-testid={`cashout-msg-${c}`} style={{ fontSize: 13, color: "var(--pine)", marginTop: 8, fontWeight: 600 }}>{A.canCashout}</p>
                <p data-testid={`next-payout-${c}`} style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 4 }}>{A.nextPayout.replace("{month}", nextMonth)}</p>
              </>
            ) : (
              <p data-testid={`progress-msg-${c}`} style={{ fontSize: 13, color: "var(--muted)", marginTop: 8 }}>{A.progressMsg.replace("{x}", money(missing, c))}</p>
            )}
          </div>
        );
      })}
      {anyReady && !hasPayoutData && (
        <div data-testid="payout-data-warning" style={{ marginTop: 4, padding: 12, borderRadius: 8, background: "color-mix(in srgb, var(--brass) 14%, transparent)", border: "1px solid var(--brass)" }}>
          <p style={{ fontSize: 13, marginBottom: 8 }}>{A.needPayoutData}</p>
          <button className="btn sm primary" data-testid="go-to-payout-form" onClick={onGoToForm}>{A.goToForm}</button>
        </div>
      )}
    </div>
  );
}

function Simulator({ A, data }) {
  const plans = (data.sim_plans || []).filter((p) => p.monthly > 0);
  const [planId, setPlanId] = useState(plans[0]?.id || "");
  const plan = plans.find((p) => p.id === planId) || plans[0];
  const rateM = data.rates?.month || 8, rateY = data.rates?.year || 10;
  if (!plan) return null;
  return (
    <div className="card" data-testid="affiliate-simulator" style={{ padding: 16 }}>
      <h2 style={{ marginBottom: 4 }}>{A.simTitle}</h2>
      <p className="eyebrow" style={{ marginBottom: 12 }}>{A.simIntro}</p>
      <label className="eyebrow" style={{ display: "block", marginBottom: 6 }}>{A.simPlan}</label>
      <select className="sel" data-testid="sim-plan" value={plan.id} onChange={(e) => setPlanId(e.target.value)} style={{ marginBottom: 14 }}>
        {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · {money(p.monthly, p.currency)}/mo</option>)}
      </select>
      <div style={{ display: "grid", gap: 8 }}>
        {PEOPLE.map((n) => {
          const em = n * plan.monthly * rateM / 100;
          const ea = n * plan.annual * rateY / 100;
          return (
            <div key={n} data-testid={`sim-row-${n}`} style={{ display: "grid", gap: 4, borderTop: "1px solid var(--line)", paddingTop: 8 }}>
              <div style={{ fontSize: 13.5 }}>{A.simMonthlyUsers.replace("{n}", n).replace("{v}", money(em, plan.currency))}</div>
              <div style={{ fontSize: 13.5 }}>{A.simAnnualUsers.replace("{n}", n).replace("{v}", money(ea, plan.currency))}</div>
            </div>
          );
        })}
      </div>
      <p style={{ color: "var(--muted)", fontSize: 12, marginTop: 12 }}>{A.simNoteTpl.replace("{y}", rateY).replace("{m}", rateM)}</p>
    </div>
  );
}

function Rules({ A }) {
  return (
    <div className="card" data-testid="affiliate-rules" style={{ padding: 16 }}>
      <h2 style={{ marginBottom: 10 }}>{A.rulesTitle}</h2>
      <ul style={{ display: "grid", gap: 6, fontSize: 13.5, color: "var(--ink)", listStyle: "disc", paddingLeft: 18 }}>
        {A.rules.map((r, i) => <li key={i} data-testid={`rule-${i}`}>{r}</li>)}
      </ul>
    </div>
  );
}

function AdminPanel({ A }) {
  const { locale } = useI18n();
  const [ov, setOv] = useState(null);
  const [cfg, setCfg] = useState({ rate_year: 10, rate_month: 8, min_payout: 20 });
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/affiliate/overview");
    setOv(data);
    const c = await api.get("/admin/affiliate/config");
    setCfg({ rate_year: c.data.rate_year, rate_month: c.data.rate_month, min_payout: c.data.min_payout });
  }, []);
  useEffect(() => { load(); }, [load]);
  const saveCfg = async () => {
    try { await api.put("/admin/affiliate/config", cfg); toast.success(A.savedOk); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const markPaid = async (id) => {
    const reference = window.prompt(A.reference) || "";
    try { await api.post(`/admin/payouts/${id}/pay`, { reference }); toast.success(A.markPaid); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const downloadCsv = async () => {
    const res = await api.get("/admin/affiliate/commissions.csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "lura_commissions.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const downloadBatchCsv = async () => {
    const res = await api.get("/admin/affiliate/batch.csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "lura_lote_pago.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const generateBatch = async () => {
    if (!window.confirm(A.batchHint)) return;
    try {
      const { data } = await api.post("/admin/affiliate/batch/generate", {});
      toast.success(`${A.batchGenerated}: ${data.count} (mín. ${data.min_payout})`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const [special, setSpecial] = useState({});
  const saveSpecial = async (a) => {
    const raw = special[a.id] ?? a.special_pct ?? "";
    const pct = raw === "" ? 0 : Number(raw);
    if (!window.confirm(`¿Fijar tasa especial de ${a.code} en ${pct || "ninguna"}%? Solo el admin puede cambiarla. Queda en auditoría.`)) return;
    try { await api.patch(`/admin/affiliates/${a.id}`, { special_pct: pct }); toast.success(A.savedOk); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [reassign, setReassign] = useState({ email: "", aff_id: "" });
  const [redir, setRedir] = useState({});
  const doReassign = async () => {
    const email = reassign.email.trim(); const aff_id = reassign.aff_id;
    if (!email || !aff_id) { toast.error("Indica email y código de afiliado destino"); return; }
    const code = (ov.affiliates.find((a) => a.id === aff_id) || {}).code;
    if (!window.confirm(`¿Reasignar a ${email} como referido de ${code}?\nSe conserva su fecha de alta. Sin comisiones retroactivas. Queda en auditoría.`)) return;
    try { const { data } = await api.post(`/admin/affiliates/${aff_id}/reassign`, { email });
      toast.success(`Reasignado a ${data.to_code} (alta ${(data.kept_signup_at || "").slice(0, 10)})`);
      setReassign({ email: "", aff_id: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleStatus = async (a) => {
    const next = a.status === "inactive" ? "active" : "inactive";
    if (!window.confirm(`¿Cambiar el código ${a.code} a "${next}"?`)) return;
    try { await api.patch(`/admin/affiliates/${a.id}`, { status: next }); toast.success("Estado actualizado"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveRedirect = async (a) => {
    const to = (redir[a.id] ?? a.redirect_to_code ?? "").trim();
    if (!window.confirm(to ? `¿Redirigir la atribución de ${a.code} → ${to.toUpperCase()}?` : `¿Quitar la redirección de ${a.code}?`)) return;
    try { await api.patch(`/admin/affiliates/${a.id}`, { redirect_to_code: to }); toast.success("Redirección guardada"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!ov) return <Spinner />;
  const money2 = (v, c) => money(Number(v || 0), c);
  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div className="card" data-testid="admin-aff-actions" style={{ padding: 16 }}>
        <h2 style={{ marginBottom: 4 }}>Reasignar referido</h2>
        <p className="eyebrow" style={{ marginBottom: 12 }}>Vincula un usuario ya registrado a un afiliado, conservando su alta. No genera comisiones retroactivas.</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={{ flex: "1 1 240px" }}><div className="eyebrow">Email del referido</div>
            <input className="sel" data-testid="reassign-email" value={reassign.email} onChange={(e) => setReassign({ ...reassign, email: e.target.value })} placeholder="persona@email.com" /></label>
          <label style={{ flex: "1 1 200px" }}><div className="eyebrow">Afiliado destino</div>
            <select className="sel" data-testid="reassign-aff" value={reassign.aff_id} onChange={(e) => setReassign({ ...reassign, aff_id: e.target.value })}>
              <option value="">Elegir código…</option>
              {ov.affiliates.filter((a) => a.status !== "inactive").map((a) => <option key={a.id} value={a.id}>{a.code}{a.email ? ` · ${a.email}` : ""}</option>)}
            </select></label>
          <button className="btn primary" data-testid="reassign-submit" onClick={doReassign}>Reasignar</button>
        </div>
      </div>

      <div className="card" data-testid="admin-aff-config" style={{ padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>{A.ratesTitle}</h2>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label><div className="eyebrow">{A.rateYear}</div><input className="sel" type="number" data-testid="cfg-rate-year" value={cfg.rate_year} onChange={(e) => setCfg({ ...cfg, rate_year: Number(e.target.value) })} /></label>
          <label><div className="eyebrow">{A.rateMonth}</div><input className="sel" type="number" data-testid="cfg-rate-month" value={cfg.rate_month} onChange={(e) => setCfg({ ...cfg, rate_month: Number(e.target.value) })} /></label>
          <label><div className="eyebrow">{A.minPayout}</div><input className="sel" type="number" data-testid="cfg-min-payout" value={cfg.min_payout} onChange={(e) => setCfg({ ...cfg, min_payout: Number(e.target.value) })} /></label>
          <button className="btn primary" data-testid="cfg-save" onClick={saveCfg}>{A.save}</button>
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div className="card-h" style={{ padding: 0, marginBottom: 12 }}><h2>{A.affiliatesList} ({ov.affiliates.length})</h2>
          <button className="btn sm" data-testid="admin-csv" onClick={downloadCsv}>{A.csv}</button></div>
        <div style={{ overflowX: "auto" }}>
          <table className="w-full text-sm" data-testid="admin-affiliates-table">
            <thead><tr style={{ textAlign: "left", color: "var(--muted)", fontSize: 12 }}>
              <th style={{ padding: 6 }}>{A.code}</th><th style={{ padding: 6 }}>Email</th><th style={{ padding: 6 }}>{A.referrals}</th><th style={{ padding: 6 }}>{A.available}</th><th style={{ padding: 6 }}>{A.specialRate}</th><th style={{ padding: 6 }}>Estado / redirección</th>
            </tr></thead>
            <tbody>
              {ov.affiliates.map((a) => (
                <tr key={a.id} data-testid={`admin-aff-${a.id}`} style={{ borderTop: "1px solid var(--line)" }}>
                  <td style={{ padding: 6, fontWeight: 600 }}>{a.code}</td>
                  <td style={{ padding: 6 }}>{a.email}</td>
                  <td style={{ padding: 6 }}>{a.referrals_count}</td>
                  <td style={{ padding: 6 }} className="num">{Object.entries(a.balances || {}).map(([c, b]) => `${money2(b.available, c)}`).join(" · ") || "—"}</td>
                  <td style={{ padding: 6 }}>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <input className="sel" type="number" min="0" max="100" style={{ width: 70 }}
                        data-testid={`aff-special-input-${a.id}`}
                        defaultValue={a.special_pct ?? ""} placeholder="—"
                        onChange={(e) => setSpecial({ ...special, [a.id]: e.target.value })} />
                      <span style={{ fontSize: 12, color: "var(--muted)" }}>%</span>
                      <button className="btn sm" data-testid={`aff-special-save-${a.id}`} onClick={() => saveSpecial(a)}>{A.save}</button>
                    </div>
                  </td>
                  <td style={{ padding: 6 }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <button className="btn sm" data-testid={`aff-toggle-${a.id}`} onClick={() => toggleStatus(a)}>
                        {a.status === "inactive" ? "Reactivar" : "Desactivar"}
                      </button>
                      <input className="sel" data-testid={`aff-redirect-input-${a.id}`} style={{ width: 120 }}
                        defaultValue={a.redirect_to_code || ""} placeholder="redirige a…"
                        onChange={(e) => setRedir({ ...redir, [a.id]: e.target.value })} />
                      <button className="btn sm" data-testid={`aff-redirect-save-${a.id}`} onClick={() => saveRedirect(a)}>Guardar</button>
                      {a.status === "inactive" && <span className="chip" style={{ fontSize: 11 }}>inactivo</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div className="card-h" style={{ padding: 0, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <h2>{A.batchTitle} ({ov.payouts.length})</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn sm primary" data-testid="generate-batch" onClick={generateBatch}>{A.genBatch}</button>
            <button className="btn sm" data-testid="batch-csv" onClick={downloadBatchCsv}>{A.batchCsv}</button>
          </div>
        </div>
        <p className="eyebrow" style={{ marginBottom: 12 }}>{A.batchHint}</p>
        {ov.payouts.length === 0 ? <p style={{ color: "var(--muted)", fontSize: 13.5 }}>{A.noPayouts}</p> : (
          <div style={{ overflowX: "auto" }}><table className="w-full text-sm" data-testid="admin-payouts-table">
            <thead><tr style={{ textAlign: "left", color: "var(--muted)", fontSize: 12 }}>
              <th style={{ padding: 6 }}>{A.code}</th><th style={{ padding: 6 }}>{A.amount}</th><th style={{ padding: 6 }}>IBAN</th><th style={{ padding: 6 }}>PayPal</th><th style={{ padding: 6 }}>{A.status}</th><th style={{ padding: 6 }}></th>
            </tr></thead>
            <tbody>
              {ov.payouts.map((p) => (
                <tr key={p.id} data-testid={`admin-payout-${p.id}`} style={{ borderTop: "1px solid var(--line)" }}>
                  <td style={{ padding: 6, fontWeight: 600 }}>{p.affiliate_code || "—"}</td>
                  <td style={{ padding: 6 }} className="num">{money2(p.amount, p.currency)}</td>
                  <td style={{ padding: 6 }}>{p.iban || "—"}</td>
                  <td style={{ padding: 6 }}>{p.paypal || "—"}</td>
                  <td style={{ padding: 6 }}>{p.status === "paid" ? A.paid : A.pending}</td>
                  <td style={{ padding: 6 }}>{p.status !== "paid" && <button className="btn sm" data-testid={`mark-paid-${p.id}`} onClick={() => markPaid(p.id)}>{A.markPaid}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

export default function Affiliates() {
  const { user } = useAuth();
  const { locale } = useI18n();
  const A = affT(locale);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("my");
  const [busy, setBusy] = useState(false);
  const [pd, setPd] = useState({ iban: "", paypal: "", holder: "" });
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    const { data } = await api.get("/affiliates/me");
    setData(data);
    // No prefijamos iban/paypal (llegan enmascarados); el titular sí (no es sensible). En blanco = conservar.
    if (data.payout_details) setPd({ iban: "", paypal: "", holder: data.payout_details.holder || "" });
  }, []);
  useEffect(() => { load(); }, [load]);

  const activate = async () => {
    setBusy(true);
    try { await api.post("/affiliates/activate"); await load(); toast.success(A.activate); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(data.link); toast.success(A.copied); } catch { /* noop */ }
  };
  const waShare = () => window.open(`https://wa.me/?text=${encodeURIComponent(A.promise + " " + data.link)}`, "_blank");
  const emailShare = () => { window.location.href = `mailto:?subject=${encodeURIComponent("Lura")}&body=${encodeURIComponent(A.promise + "\n\n" + data.link)}`; };

  const savePd = async () => {
    try { await api.patch("/affiliates/me/payout-details", pd); toast.success(A.savedOk); setPd((p) => ({ ...p, iban: "", paypal: "" })); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <Spinner />;
  const det = data.payout_details || {};

  return (
    <section className="view" data-testid="affiliates-page">
      <div className="page-h">
        <div>
          <h1>{A.title}</h1>
          <p>{A.promise}</p>
        </div>
      </div>

      {isAdmin && (
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          <button className={`btn sm ${tab === "my" ? "primary" : ""}`} data-testid="tab-my" onClick={() => setTab("my")}>{A.myTab}</button>
          <button className={`btn sm ${tab === "admin" ? "primary" : ""}`} data-testid="tab-admin" onClick={() => setTab("admin")}>{A.adminTab}</button>
        </div>
      )}

      {isAdmin && tab === "admin" ? <AdminPanel A={A} /> : !data.active ? (
        <div style={{ display: "grid", gap: 20 }}>
          <div className="card" data-testid="affiliate-empty" style={{ padding: 28, textAlign: "center" }}>
            <Gift size={28} style={{ color: "var(--brass)" }} />
            <h2 style={{ fontFamily: "var(--f-display)", fontSize: 22, marginTop: 10 }}>{A.emptyTitle}</h2>
            <p style={{ color: "var(--muted)", marginTop: 6, fontSize: 14, maxWidth: 560, marginInline: "auto" }}>{A.promise}</p>
            <button className="btn primary" data-testid="activate-affiliate" onClick={activate} disabled={busy} style={{ marginTop: 16 }}>
              {busy ? A.activating : A.activate}
            </button>
          </div>
          <Simulator A={A} data={data} />
          <Rules A={A} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 20 }}>
          <div className="card" data-testid="affiliate-link-card" style={{ padding: 16 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>{A.yourLink} · {A.code} {data.code}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input className="sel" readOnly value={data.link} data-testid="affiliate-link" style={{ flex: "1 1 280px", minWidth: 0 }} />
              <button className="btn sm primary" data-testid="copy-link" onClick={copyLink}><Copy size={14} /> {A.copy}</button>
              <button className="btn sm" data-testid="share-whatsapp" onClick={waShare}><Share2 size={14} /> {A.shareWa}</button>
              <button className="btn sm" data-testid="share-email" onClick={emailShare}>{A.shareEmail}</button>
            </div>
          </div>

          <div className="card" data-testid="affiliate-rate-card" style={{ padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <h2>{A.rateCardTitle}</h2>
              {data.rates?.special && (
                <span className="chip" data-testid="rate-parceiro-badge" title={A.parceiroTip}
                  style={{ background: "var(--brass)", color: "#fff", fontWeight: 700, letterSpacing: ".03em" }}>Parceiro Clube</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              <div><div className="eyebrow">{A.rateYear}</div><span className="num" data-testid="rate-year-value" style={{ fontSize: 22 }}>{data.rates?.year ?? 10}%</span></div>
              <div><div className="eyebrow">{A.rateMonth}</div><span className="num" data-testid="rate-month-value" style={{ fontSize: 22 }}>{data.rates?.month ?? 8}%</span></div>
            </div>
            <p style={{ color: "var(--muted)", fontSize: 12.5, marginTop: 10 }}>{data.rates?.special ? A.rateSpecialNote : A.rateStdNote}</p>
          </div>

          <div className="kgrid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
            <Stat icon={MousePointerClick} label={A.clicks} value={data.stats.clicks} testid="stat-clicks" />
            <Stat icon={Users} label={A.referrals} value={data.stats.referrals} testid="stat-referrals" />
            <Stat icon={BadgeCheck} label={A.activeSubs} value={data.stats.active} testid="stat-active" />
          </div>

          <BalanceCard A={A} balances={data.balances} det={det} minPayout={data.min_payout}
            nextMonth={new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString(locale, { month: "long", year: "numeric" })}
            onGoToForm={() => { const el = document.getElementById("payout-form"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }} />

          <div className="card" data-testid="affiliate-referrals" style={{ padding: 16 }}>
            <h2 style={{ marginBottom: 12 }}>{A.referralsTitle}</h2>
            {(!data.referrals || data.referrals.length === 0) ? (
              <p style={{ color: "var(--muted)", fontSize: 13.5 }}>{A.refNone}</p>
            ) : (
              <div style={{ overflowX: "auto" }}><table className="w-full text-sm">
                <tbody>
                  {data.referrals.map((r, i) => (
                    <tr key={i} data-testid={`referral-row-${i}`} style={{ borderTop: "1px solid var(--line)" }}>
                      <td style={{ padding: 8 }}>{r.display}</td>
                      <td style={{ padding: 8 }}><span className="chip">{r.status === "active" ? A.stActive : r.status === "trial" ? A.stTrial : A.stFree}</span></td>
                      <td style={{ padding: 8, textAlign: "right" }} className="num">{A.generated}: {money(r.generated, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>

          <div className="card" id="payout-form" data-testid="affiliate-payout" style={{ padding: 16 }}>
            <h2 style={{ marginBottom: 4 }}><Wallet size={16} style={{ verticalAlign: "-2px", color: "var(--brass)" }} /> {A.payoutTitle}</h2>
            <p style={{ color: "var(--muted)", fontSize: 12.5, marginBottom: 12 }}>{A.payoutIntro}</p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 10 }}>
              <div style={{ fontSize: 13 }}><div className="eyebrow">{A.iban} · {A.currentLabel}</div>
                <span className="num" data-testid="payout-iban-current">{det.iban || <span style={{ color: "var(--muted)" }}>{A.notSet}</span>}</span></div>
              <div style={{ fontSize: 13 }}><div className="eyebrow">{A.paypal} · {A.currentLabel}</div>
                <span className="num" data-testid="payout-paypal-current">{det.paypal || <span style={{ color: "var(--muted)" }}>{A.notSet}</span>}</span></div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 6 }}>
              <label style={{ flex: "1 1 240px" }}><div className="eyebrow">{A.iban}</div><input className="sel" data-testid="payout-iban" value={pd.iban} onChange={(e) => setPd({ ...pd, iban: e.target.value })} /></label>
              <label style={{ flex: "1 1 200px" }}><div className="eyebrow">{A.paypal}</div><input className="sel" data-testid="payout-paypal" value={pd.paypal} onChange={(e) => setPd({ ...pd, paypal: e.target.value })} /></label>
              <label style={{ flex: "1 1 200px" }}><div className="eyebrow">{A.holder}</div><input className="sel" data-testid="payout-holder" value={pd.holder} onChange={(e) => setPd({ ...pd, holder: e.target.value })} /></label>
              <button className="btn sm primary" data-testid="save-payout-details" onClick={savePd}>{A.savePayout}</button>
            </div>
            <p style={{ color: "var(--muted)", fontSize: 12, marginBottom: 12 }}>{A.keepHint}</p>
            <div style={{ marginTop: 6 }}>
              <div className="eyebrow" style={{ marginBottom: 6 }}>{A.payoutHistory}</div>
              {(!data.payouts || data.payouts.length === 0) ? <p style={{ color: "var(--muted)", fontSize: 13 }}>{A.noPayouts}</p> : (
                <div style={{ overflowX: "auto" }}><table className="w-full text-sm" data-testid="affiliate-payouts-table">
                  <thead><tr style={{ textAlign: "left", color: "var(--muted)", fontSize: 12 }}>
                    <th style={{ padding: 6 }}>{A.dateCol}</th><th style={{ padding: 6 }}>{A.amount}</th><th style={{ padding: 6 }}>{A.status}</th>
                  </tr></thead>
                  <tbody>
                    {data.payouts.map((p) => (
                      <tr key={p.id} data-testid={`payout-${p.id}`} style={{ borderTop: "1px solid var(--line)" }}>
                        <td style={{ padding: 6, color: "var(--muted)" }}>{(p.paid_at || p.created_at || "").slice(0, 10)}</td>
                        <td style={{ padding: 6 }} className="num">{money(p.amount, p.currency)}</td>
                        <td style={{ padding: 6 }}><span className="chip">{p.status === "paid" ? A.paid : A.pending}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          </div>

          <Simulator A={A} data={data} />
          <Rules A={A} />
        </div>
      )}
    </section>
  );
}
